import React, { useState } from "react";
import {UploadedFile} from "../../api/file_api";
import CropAndPositionPopover from "../../common/components/crop-and-position-popover";
import cachedImageElement from "@src/lib/cached-image-element";
import {TrainingAnalysisImageResult} from "../../api/training_analysis_api";

interface ImageContainerProps {
    image: UploadedFile;
    id: string;
    onRemove: () => void;
    onClick: () => void;
    onCrop?: (croppedFile: File) => void;
    uploadFile?: (file: File) => Promise<UploadedFile>;
    imageAnalysis?: TrainingAnalysisImageResult;
}

const ImageContainer = ({id, image, onRemove, onClick, onCrop, uploadFile, imageAnalysis}: ImageContainerProps) => {
    const [imageElement] = useState<HTMLImageElement | undefined>(image.permalink ? cachedImageElement(image.permalink) : undefined);
    const hasIssues = imageAnalysis && imageAnalysis.quality_issues && imageAnalysis.quality_issues.length > 0;
    
    return (
        <div id={id} className={`relative cursor-pointer group border-2 rounded-lg p-2 ${
            hasIssues ? 'border-red-500' : 'border-gray-300'
        }`}>
            <img id={image.id} crossOrigin="anonymous" src={image.permalink} alt={image.name} className="w-full object-cover rounded-lg" onClick={onClick}/>
            
            {/* Image Analysis Section */}
            {imageAnalysis && (
                <div className="mt-2 space-y-2 text-xs">
                    {/* Quality Score */}
                    {imageAnalysis.quality_score !== undefined && (
                        <div className="flex justify-between items-center">
                            <strong className="text-gray-600">Quality Score</strong>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                imageAnalysis.quality_score >= 0.8 ? "bg-green-100 text-green-700" :
                                imageAnalysis.quality_score >= 0.6 ? "bg-yellow-100 text-yellow-700" :
                                "bg-red-100 text-red-700"
                            }`}>
                                {(imageAnalysis.quality_score * 100).toFixed(0)}%
                            </span>
                        </div>
                    )}
                    
                    {/* Quality Issues */}
                    {hasIssues && (
                        <div>
                            <strong className="text-gray-600 block mb-1">Quality Issues</strong>
                            <div className="space-y-1">
                                {imageAnalysis.quality_issues.map((issue, issueIdx) => (
                                    <div key={issueIdx} className="text-red-600 bg-red-50 px-2 py-1 rounded">
                                        {issue}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Image Details */}
                    <div className="space-y-1 text-gray-600">
                        {imageAnalysis.angle && (
                            <div><strong>Angle</strong> {imageAnalysis.angle}</div>
                        )}
                        {imageAnalysis.expression && (
                            <div><strong>Expression</strong> {imageAnalysis.expression}</div>
                        )}
                        {imageAnalysis.view && (
                            <div><strong>View</strong> {imageAnalysis.view}</div>
                        )}
                        {imageAnalysis.outfit && (
                            <div><strong>Outfit</strong> {imageAnalysis.outfit}</div>
                        )}
                    </div>
                </div>
            )}
            
            <div className="mt-2 flex gap-2 justify-center">
                {onCrop && (
                    <CropAndPositionPopover
                        src={imageElement?.src || ""}
                        onSave={onCrop}
                        uploadFile={uploadFile || (() => Promise.resolve({} as UploadedFile))}
                        trigger={
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                className="bg-blue-600 rounded px-3 py-1 hover:bg-blue-700 transition-colors text-white text-sm"
                                title="Crop Image"
                            >
                                Crop
                            </button>
                        }
                    />
                )}
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="bg-red-600 rounded px-3 py-1 hover:bg-red-700 transition-colors text-white text-sm"
                    title="Delete Image"
                >
                    Delete
                </button>
            </div>
        </div>
    )
}
export default ImageContainer;